import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BeneficialCalculator = ({ onCalculationComplete, isPreviewMode = false }) => {
  const [formData, setFormData] = useState({
    pestType: '',
    fieldSize: '',
    fieldUnit: 'sqft',
    pestPressure: '3',
    beneficialSpecies: '',
    environmentalConditions: 'optimal',
    currentTemperature: '',
    humidity: '',
    releaseMethod: 'broadcast'
  });

  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Example data for non-premium users
  const exampleData = {
    pestType: 'spider-mites',
    fieldSize: '1000',
    fieldUnit: 'sqft',
    pestPressure: '3',
    beneficialSpecies: 'phytoseiulus-persimilis',
    environmentalConditions: 'optimal',
    currentTemperature: '72',
    humidity: '65',
    releaseMethod: 'broadcast'
  };

  const exampleResults = {
    totalQuantity: 2000,
    quantityPerArea: 2000,
    costEstimate: '4.00',
    releaseSchedule: [
      { week: 1, quantity: 1200, action: 'Initial release' },
      { week: 3, quantity: 800, action: 'Follow-up release' }
    ],
    effectiveness: 0.9,
    warnings: []
  };

  const pestTypes = [
    { value: 'spider-mites', label: 'Spider Mites' },
    { value: 'aphids', label: 'Aphids' },
    { value: 'thrips', label: 'Thrips' },
    { value: 'whiteflies', label: 'Whiteflies' },
    { value: 'fungus-gnats', label: 'Fungus Gnats' },
    { value: 'mealybugs', label: 'Mealybugs' }
  ];

  const beneficialSpecies = [
    { value: 'amblyseius-cucumeris', label: 'Amblyseius cucumeris (Thrips, Mites)', pests: ['thrips', 'spider-mites'] },
    { value: 'stratiolaelaps', label: 'Stratiolaelaps scimitus (Fungus Gnats)', pests: ['fungus-gnats'] },
    { value: 'chrysoperla-carnea', label: 'Chrysoperla carnea (Green Lacewing)', pests: ['aphids', 'thrips', 'mealybugs'] },
    { value: 'encarsia-formosa', label: 'Encarsia formosa (Whiteflies)', pests: ['whiteflies'] },
    { value: 'phytoseiulus-persimilis', label: 'Phytoseiulus persimilis (Spider Mites)', pests: ['spider-mites'] },
    { value: 'aphidius-colemani', label: 'Aphidius colemani (Aphids)', pests: ['aphids'] }
  ];

  const fieldUnits = [
    { value: 'sqft', label: 'Square Feet' },
    { value: 'sqm', label: 'Square Meters' },
    { value: 'acres', label: 'Acres' },
    { value: 'plants', label: 'Number of Plants' }
  ];

  const environmentalConditions = [
    { value: 'optimal', label: 'Optimal (70-75°F, 60-70% RH)' },
    { value: 'good', label: 'Good (65-80°F, 50-75% RH)' },
    { value: 'challenging', label: 'Challenging (Outside optimal range)' }
  ];

  const releaseMethodOptions = [
    { value: 'broadcast', label: 'Broadcast Release' },
    { value: 'targeted', label: 'Targeted Hot Spots' },
    { value: 'preventive', label: 'Preventive Application' }
  ];

  const calculateBeneficialRelease = () => {
    // If in preview mode, show example results immediately
    if (isPreviewMode) {
      setResults(exampleResults);
      return;
    }

    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const fieldSizeNum = parseFloat(formData?.fieldSize);
      let baseRate = 0;
      let multiplier = 1;

      // Base rates per 1000 sq ft
      const baseRates = {
        'amblyseius-cucumeris': 25000,
        'stratiolaelaps': 10000,
        'chrysoperla-carnea': 5000,
        'encarsia-formosa': 3000,
        'phytoseiulus-persimilis': 2000,
        'aphidius-colemani': 500
      };

      baseRate = baseRates?.[formData?.beneficialSpecies] || 10000;

      // Adjust for field size unit
      let adjustedFieldSize = fieldSizeNum;
      if (formData?.fieldUnit === 'sqm') adjustedFieldSize = fieldSizeNum * 10.764;
      if (formData?.fieldUnit === 'acres') adjustedFieldSize = fieldSizeNum * 43560;
      if (formData?.fieldUnit === 'plants') adjustedFieldSize = fieldSizeNum * 4; // Assume 4 sq ft per plant

      // Adjust for pest pressure
      const pressureMultipliers = { '1': 0.5, '2': 0.75, '3': 1, '4': 1.5, '5': 2 };
      multiplier *= pressureMultipliers?.[formData?.pestPressure] || 1;

      // Adjust for environmental conditions
      const environmentMultipliers = { 'optimal': 1, 'good': 1.25, 'challenging': 1.5 };
      multiplier *= environmentMultipliers?.[formData?.environmentalConditions] || 1;

      // Adjust for release method
      const methodMultipliers = { 'broadcast': 1, 'targeted': 0.7, 'preventive': 0.8 };
      multiplier *= methodMultipliers?.[formData?.releaseMethod] || 1;

      const totalQuantity = Math.round((baseRate * (adjustedFieldSize / 1000)) * multiplier);
      const costEstimate = totalQuantity * 0.002; // Rough cost per beneficial
      
      const calculationResults = {
        totalQuantity,
        quantityPerArea: Math.round(totalQuantity / (adjustedFieldSize / 1000)),
        costEstimate: costEstimate?.toFixed(2),
        releaseSchedule: generateReleaseSchedule(totalQuantity),
        effectiveness: calculateEffectiveness(),
        warnings: generateWarnings()
      };

      setResults(calculationResults);
      
      // Save to history
      if (onCalculationComplete) {
        onCalculationComplete({
          pestType: formData?.pestType,
          beneficialSpecies: formData?.beneficialSpecies,
          fieldSize: `${formData?.fieldSize} ${formData?.fieldUnit}`,
          totalQuantity,
          costEstimate: calculationResults?.costEstimate
        });
      }

      setIsCalculating(false);
    }, 1500);
  };

  const generateReleaseSchedule = (totalQuantity) => {
    return [
      { week: 1, quantity: Math.round(totalQuantity * 0.6), action: 'Initial release' },
      { week: 3, quantity: Math.round(totalQuantity * 0.4), action: 'Follow-up release' }
    ];
  };

  const calculateEffectiveness = () => {
    const selectedSpecies = beneficialSpecies?.find(s => s?.value === formData?.beneficialSpecies);
    const isOptimalMatch = selectedSpecies?.pests?.includes(formData?.pestType);
    const environmentFactor = formData?.environmentalConditions === 'optimal' ? 0.9 : 
                             formData?.environmentalConditions === 'good' ? 0.75 : 0.6;
    
    return isOptimalMatch ? environmentFactor : environmentFactor * 0.7;
  };

  const generateWarnings = () => {
    const warnings = [];
    
    if (formData?.environmentalConditions === 'challenging') {
      warnings?.push('Environmental conditions may reduce beneficial effectiveness');
    }
    
    if (parseFloat(formData?.currentTemperature) > 85 || parseFloat(formData?.currentTemperature) < 60) {
      warnings?.push('Temperature outside optimal range for most beneficial insects');
    }
    
    const selectedSpecies = beneficialSpecies?.find(s => s?.value === formData?.beneficialSpecies);
    if (selectedSpecies && !selectedSpecies?.pests?.includes(formData?.pestType)) {
      warnings?.push('Selected beneficial may not be optimal for this pest type');
    }

    return warnings;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResults(null); // Clear results when inputs change
  };

  const resetForm = () => {
    setFormData({
      pestType: '',
      fieldSize: '',
      fieldUnit: 'sqft',
      pestPressure: '3',
      beneficialSpecies: '',
      environmentalConditions: 'optimal',
      currentTemperature: '',
      humidity: '',
      releaseMethod: 'broadcast'
    });
    setResults(null);
  };

  const loadExampleData = () => {
    setFormData(exampleData);
    setResults(null);
  };

  const isFormValid = formData?.pestType && formData?.fieldSize && formData?.beneficialSpecies;

  return (
    <div className="space-y-6">
      {/* Preview Mode Banner for Non-Premium Users */}
      {isPreviewMode && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Example Preview Mode</h3>
              <p className="text-sm text-blue-700 mb-3">
                You're viewing an example calculation with sample data. Upgrade to Premium to use custom inputs and save your calculations.
              </p>
              <Button
                size="sm"
                onClick={loadExampleData}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                iconName="Play"
                iconPosition="left"
              >
                Load Example Data
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Input Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pest Type *
            </label>
            <Select
              value={formData?.pestType}
              onChange={(value) => handleInputChange('pestType', value)}
              options={pestTypes}
              placeholder="Select pest type"
              disabled={isPreviewMode}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field Size *
              </label>
              <Input
                type="number"
                value={formData?.fieldSize}
                onChange={(e) => handleInputChange('fieldSize', e?.target?.value)}
                placeholder="Enter size"
                min="0"
                disabled={isPreviewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <Select
                value={formData?.fieldUnit}
                onChange={(value) => handleInputChange('fieldUnit', value)}
                options={fieldUnits}
                disabled={isPreviewMode}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pest Pressure Level: {formData?.pestPressure}/5
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={formData?.pestPressure}
              onChange={(e) => handleInputChange('pestPressure', e?.target?.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              disabled={isPreviewMode}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beneficial Species *
            </label>
            <Select
              value={formData?.beneficialSpecies}
              onChange={(value) => handleInputChange('beneficialSpecies', value)}
              options={beneficialSpecies}
              placeholder="Select beneficial insect"
              disabled={isPreviewMode}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Environmental Conditions
            </label>
            <Select
              value={formData?.environmentalConditions}
              onChange={(value) => handleInputChange('environmentalConditions', value)}
              options={environmentalConditions}
              disabled={isPreviewMode}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temperature (°F)
              </label>
              <Input
                type="number"
                value={formData?.currentTemperature}
                onChange={(e) => handleInputChange('currentTemperature', e?.target?.value)}
                placeholder="Optional"
                min="0"
                max="120"
                disabled={isPreviewMode}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Humidity (%)
              </label>
              <Input
                type="number"
                value={formData?.humidity}
                onChange={(e) => handleInputChange('humidity', e?.target?.value)}
                placeholder="Optional"
                min="0"
                max="100"
                disabled={isPreviewMode}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Method
            </label>
            <Select
              value={formData?.releaseMethod}
              onChange={(value) => handleInputChange('releaseMethod', value)}
              options={releaseMethodOptions}
              disabled={isPreviewMode}
            />
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={resetForm}
          className="border-gray-300"
          disabled={isPreviewMode}
        >
          Reset Form
        </Button>
        
        <Button
          onClick={calculateBeneficialRelease}
          disabled={(!isFormValid && !isPreviewMode) || isCalculating}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {isCalculating ? (
            <>
              <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Icon name="Calculator" size={16} className="mr-2" />
              {isPreviewMode ? 'View Example Results' : 'Calculate Release'}
            </>
          )}
        </Button>
      </div>
      {/* Results */}
      {results && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
          {isPreviewMode && (
            <div className="mb-4 bg-blue-100 border border-blue-300 rounded-lg p-3">
              <p className="text-sm text-blue-800 flex items-center">
                <Icon name="Eye" size={16} className="mr-2" />
                <strong>Example Results:</strong>&nbsp;These calculations are based on sample data. Upgrade to Premium for full access.
              </p>
            </div>
          )}

          <div className="flex items-center space-x-3 mb-4">
            <Icon name="CheckCircle" size={20} className="text-green-600" />
            <h3 className="text-lg font-semibold text-green-900">Calculation Results</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{results?.totalQuantity?.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Total Beneficial Insects</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{results?.quantityPerArea?.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Per 1,000 {formData?.fieldUnit}</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">${results?.costEstimate}</div>
              <div className="text-sm text-gray-600">Estimated Cost</div>
            </div>
          </div>

          {/* Release Schedule */}
          <div className="mb-6">
            <h4 className="font-semibold text-green-900 mb-3">Recommended Release Schedule</h4>
            <div className="space-y-2">
              {results?.releaseSchedule?.map((release, index) => (
                <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-green-600">{release?.week}</span>
                    </div>
                    <span className="text-gray-900">{release?.action}</span>
                  </div>
                  <span className="font-medium text-green-600">{release?.quantity?.toLocaleString()} insects</span>
                </div>
              ))}
            </div>
          </div>

          {/* Effectiveness Indicator */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-900">Predicted Effectiveness</span>
              <span className="text-sm font-medium text-green-600">{(results?.effectiveness * 100)?.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${results?.effectiveness * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Warnings */}
          {results?.warnings && results?.warnings?.length > 0 && (
            <div className="border-t border-green-200 pt-4">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                <Icon name="AlertTriangle" size={16} className="mr-2" />
                Important Considerations
              </h4>
              <ul className="space-y-1">
                {results?.warnings?.map((warning, index) => (
                  <li key={index} className="text-sm text-yellow-700">• {warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BeneficialCalculator;